/**
 * Created by Alican Köker on 2018-06-27.
 */
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IBetLineWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite} from "gsap";
import {Fruits} from "../../Fruits";
import {FruitsGameConfig} from "../../FruitsGameConfig";
import {FruitsSoundConfig} from "../../FruitsSoundConfig";
import {FruitsParsedGameData} from "../../server/data/FruitsParsedGameData";
import {IWinLineConfig, WinLine, WinText} from "../WinLines/WinLine";
import {FruitsHelper} from "../../utils/FruitsHelper";

export class FruitsInitialWPView extends BaseView {

    private _lineAnimCont:PIXI.Container;
    private _lineTextContainer:PIXI.Container;
    private _initLinesvisible:boolean = false;
    private _animatedLines:IBetLineWinData[];
    private _winAnimCont:PIXI.Container;
    private _stars:TimelineSprite;
    private _splash:PIXI.Sprite;
    private _gamedata:FruitsParsedGameData;

    private _winText:PIXI.BitmapText;
    public static readonly HIDE_LINES:string = "fruitsInitialWPView_hideLines";

    private _lineColorList:number[] = [
        0xff3399,
        0xff3333,
        0xfd8c19,
        0xccffcc,
        0xffff00,
        0x00cc00,
        0x00cc66,
        0x00ccff,
        0x227bff,
        0xcc33ff,
    ];

    private _lineConfig:IWinLineConfig = {
        lineThickness : 14,
        baseColor : 0x46D260,
        dynamicColor : true,
        dynamicColors : this._lineColorList,
    };

    private _orientation:string = Orientation.LANDSCAPE;
    private _layer:ScalableLayer;

    constructor() {
        super();

        Fruits.initialView = this;

        this._layer = StageManager.getLayer(FruitsGameConfig.instance.LAYERS.WIN_PRESENTATIONS.name);
        EventHandler.addEventListener(this, FruitsInitialWPView.HIDE_LINES, (event:GameEvent) => this.hideLines());

        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event:GameEvent) => this.onGameDataParsed(event.params[0]));

    }

    protected onGameDataParsed(data:FruitsParsedGameData):void {
        this._gamedata = data;
    }

    protected initAnimations():void {
        this._lineAnimCont = new PIXI.Container();
        this._layer.addChild(this._lineAnimCont);
        this._lineTextContainer = new PIXI.Container();
        this._layer.addChild(this._lineTextContainer);
        this._winAnimCont = new PIXI.Container();
        this._layer.addChild(this._winAnimCont);
        this.initiateVisualObjects();

    }

    private initiateVisualObjects():void {
        this._splash = new PIXI.Sprite(GameResources.getTextures("winFXSplash")[0]);
        this._splash.blendMode = PIXI.BLEND_MODES.ADD;
        this._splash.scale.set(0.4, 0.4);
        this._splash.alpha = 0;
        this._splash.anchor.set(0.5, 0.5);
        this._winAnimCont.addChild(this._splash);

        this._stars = new TimelineSprite(GameResources.getTextures("stars"));
        this._stars.hide();
        this._stars.blendMode = PIXI.BLEND_MODES.ADD;
        this._stars.anchor.set(0.5, 0.5);
        this._winAnimCont.addChild(this._stars);

        this._winText = new PIXI.BitmapText("0", {
            fontName : "WinNumbersBM",
            fontSize : 32,
            align : "left",
        });
        this._winText.scale.set(0);
        this._winText.pivot.set(this._winText.width / 2, this._winText.height / 2);
        this._winText.visible = false;
        this._winText.skew.x = 100;
        this._winText.skew.y = 100;
        this._winAnimCont.addChild(this._winText);
    }

    protected onResize(resizeData:IResizeData):void {
        this._stars.position.set(FruitsGameConfig.instance.GAME_WIDTH / 2, FruitsGameConfig.instance.GAME_HEIGHT / 2);
        this._splash.position.set(FruitsGameConfig.instance.GAME_WIDTH / 2, FruitsGameConfig.instance.GAME_HEIGHT / 2);
        this._winText.position.set(FruitsGameConfig.instance.GAME_WIDTH / 2, FruitsGameConfig.instance.GAME_HEIGHT / 2);

        if(resizeData.orientation === Orientation.PORTRAIT) {
            this._orientation = Orientation.PORTRAIT;
        } else {
            this._orientation = Orientation.LANDSCAPE;
        }

        //if the orientation changes during building up lines, erases drawn lines, redraws them in the new orientation
        //and keeps building up the rest on new based on the new orientation
        if(this._initLinesvisible) {
            let ind:number = 0;

            if(this._animatedLines) {
                this._lineAnimCont.removeChildren();
                this._animatedLines.forEach(lineData => {
                    let line:WinLine = new WinLine(this._orientation);
                    line.drawWinningLine(lineData.betLine, lineData.betLineIndex, this._lineConfig);
                    ind++;
                    this._lineAnimCont.addChild(line);
                });
            }
        }

        this._lineTextContainer.children.length = 0;
    }

    public buildUpWinningLines(winData:IBetLineWinData[] | null):TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        this._initLinesvisible = true;
        this._animatedLines = [];

        let delay = 0.3;

        winData!.forEach((data, index) => {
            if(index < 16) {
                tl.add([
                    () => SlotGame.sound.playEffect(FruitsSoundConfig.BETLINE),
                    () => this.createLines(data),
                ], `+= ${delay}`);
            } else {
                tl.add(() => this.createLines(data));
            }

            delay *= 0.85;
        });

        return tl;
    }

    private createLines(data:IBetLineWinData):void {
        let line = new WinLine(this._orientation);
        this._lineAnimCont.addChild(line);
        line.drawWinningLine(data.betLine, data.betLineIndex, this._lineConfig);
        this._animatedLines.push(data);
        const winText = new WinText(data);
        let pos:number[] = FruitsHelper.getSymbolPosition(2, data.betLine[2], this._orientation);
        winText.position.set(pos[0], pos[1]);
        this._lineTextContainer.addChild(winText);
        const tl = new TimelineLite();
        tl.add([
            TweenLite.to(winText, 1, {y : pos[1] - 100}),
            TweenLite.to(winText, 0.8, {alpha : 0, delay : 0.2})
        ]);
        tl.add(() => {
            winText.parent.removeChild(winText);
            winText.destroy();
        });
    }

    public resetWinAnimation():void {
        this._splash.scale.set(0.4, 0.4);
        this._splash.alpha = 0;
        this._stars.scale.set(1, 1);
        this._stars.alpha = 0;
        this._stars.hide();
    }

    public hideLines():void {
        this._lineAnimCont.removeChildren();
        this._animatedLines = [];
        this._initLinesvisible = false;
    }

    public showWinAnimation(winSize:number = 3):TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        if(winSize === 0) {
            return tl;
        }

        tl.add(this.playStarsAnimation());

        if(winSize === 1) {
            return tl;
        }

        tl.add(this.playSplashAnimation(), 0.15);

        return tl;
    }

    private playSplashAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        tl.add([
            () => this._splash.alpha = 1,
            TweenLite.to(this._splash.scale, 0.15, {x : 2, y : 2})
        ]);

        tl.add(TweenLite.to(this._splash, 0.30, {alpha : 0}));

        return tl;
    }

    private playStarsAnimation():TimelineLite {
        this._stars.alpha = 1;

        const tl:TimelineLite = new TimelineLite();
        tl.add([
            () => this._stars.show(),
            this._stars.getAnimation(),
            TweenLite.to(this._stars.scale, 1, {x : 3, y : 3})
        ]);
        tl.add(TweenLite.to(this._stars, 0.2, {alpha : 0}));

        return tl;
    }
}